import { COMPETITION_ROLES, GLOBAL_ROLES, type CompetitionRole, type GlobalRole } from './roles'

/**
 * Permissions are the unit route handlers check. A handler must never test a
 * role string directly: the same account holds different roles in different
 * competitions, and a hard-coded role check in one route is how those two
 * facts drift apart.
 *
 * The fourteen names given in the Phase 2 brief are the canonical vocabulary.
 * A few extra permissions are defined alongside them, each because collapsing
 * it into one of the fourteen would grant more than intended:
 *
 *   competition.create   a brand new competition has no competition roles yet,
 *                        so this can only ever be a platform-level grant.
 *   role.read/manage     seeing who holds power is not the same as being able
 *                        to change it.
 *   team.read.all        a judge may read the teams behind assigned work only
 *   submission.read.all  through `submission.review`; an unscoped read of every
 *                        team or submission is a strictly larger privilege and
 *                        would break anonymous judging.
 *   file.read / .all     same own-versus-all split for uploaded files.
 *   result.read          reading published results is not managing them.
 *   score.read           browsing an individual judge's score/review comment
 *                        (organizer oversight) is not reopening one — kept
 *                        apart from score.manage the same way rubric read
 *                        must stay apart from rubric manage.
 *   announcement.manage  distinct from competition.manage so staff can post
 *                        notices without editing the competition itself.
 */
export const PERMISSIONS = {
  COMPETITION_READ: 'competition.read',
  COMPETITION_CREATE: 'competition.create',
  COMPETITION_MANAGE: 'competition.manage',

  FORM_MANAGE: 'form.manage',

  TEAM_READ: 'team.read',
  TEAM_READ_ALL: 'team.read.all',
  TEAM_MANAGE: 'team.manage',

  SUBMISSION_READ: 'submission.read',
  SUBMISSION_READ_ALL: 'submission.read.all',
  SUBMISSION_REVIEW: 'submission.review',
  SUBMISSION_MANAGE: 'submission.manage',

  FILE_READ: 'file.read',
  FILE_READ_ALL: 'file.read.all',
  FILE_MANAGE: 'file.manage',

  JUDGE_MANAGE: 'judge.manage',
  ASSIGNMENT_MANAGE: 'assignment.manage',

  SCORE_SUBMIT: 'score.submit',
  SCORE_READ: 'score.read',
  SCORE_MANAGE: 'score.manage',
  /**
   * Entering staff-entered ("manual") scores for a round whose scoring input
   * mode allows it. Kept apart from `score.submit` (a judge's own rubric
   * score) and `score.manage` (reopening a judge's locked score): a staff
   * member entering an attendance or penalty value must not be able to
   * reopen a judge's score, nor a judge to type staff values.
   */
  MANUAL_SCORE_ENTER: 'score.manual.enter',

  RESULT_READ: 'result.read',
  RESULT_MANAGE: 'result.manage',

  ROLE_READ: 'role.read',
  ROLE_MANAGE: 'role.manage',

  ANNOUNCEMENT_MANAGE: 'announcement.manage',

  AUDIT_READ: 'audit.read',

  /**
   * PR-197..: Member Student Eligibility & Enrollment Proof (Phase 1,
   * docs/incoming/member-student-eligibility-and-enrollment-proof-sdd.md
   * §16, GAP-ELIG-002 resolved). `eligibility.read_own`/`submit_own` govern
   * a caller's *own* eligibility record (still checked against the acting
   * user id at the route, same as `file.read` does not itself scope by
   * submission); `eligibility.review` and `eligibility.manage_policy` are
   * kept apart the same way `rubric.read`/`rubric.manage` and
   * `submission.review`/`submission.manage` are — being able to view or
   * submit your own eligibility data implies nothing about reviewing
   * someone else's, and reviewing implies nothing about changing the
   * competition-wide policy.
   */
  ELIGIBILITY_READ_OWN: 'eligibility.read_own',
  ELIGIBILITY_SUBMIT_OWN: 'eligibility.submit_own',
  ELIGIBILITY_REVIEW: 'eligibility.review',
  ELIGIBILITY_MANAGE_POLICY: 'eligibility.manage_policy',

  /**
   * v4.46 Competition Configuration JSON-only (Autonomous Execution Spec
   * G-01). `competition.config.export` is deliberately narrower than
   * `competition.manage` — it can read the full non-runtime configuration
   * of a competition the caller already administers and produce a
   * `.napaicp.json` download, but grants no write access. Kept apart from
   * `competition.manage` the same way `audit.read` is: a distinct
   * "may read a specific sensitive export" capability rather than folded
   * into the general management grant.
   */
  COMPETITION_CONFIG_EXPORT: 'competition.config.export',

  /**
   * v4.46, `docs/sdd/08-platform-governance-workflows.md` §8.1 Spec Delta.
   * `competition.clone` is checked against the SOURCE competition (the one
   * being cloned FROM) - the caller must already administer it. It does not
   * by itself authorize creating a competition from nothing; see
   * `requireExistingOrganizer` (`apps/api/src/middleware/authorize.ts`) for
   * the separate, platform-level "already an approved Organizer" gate that
   * Create-from-Template and Import-create use instead, since neither of
   * those has a source competition to scope a competition-level permission
   * against.
   */
  COMPETITION_CLONE: 'competition.clone',

  /**
   * Checked against the SOURCE competition when an organizer saves it as a
   * new Template or updates one of their Templates with a fresh snapshot
   * from this competition (Save as Template / new revision). Templates
   * themselves are not competition-scoped resources (a Template outlives
   * and is independent of the competition it was saved from), so listing
   * or reading a Template, and using it to Create from Template, are
   * authorized separately: by `requireExistingOrganizer` (platform-level)
   * plus an explicit ownership/visibility check in the route handler
   * (`private` -> creator only, `platform` -> any existing organizer) -
   * not by this permission.
   */
  TEMPLATE_MANAGE: 'competition.template.manage',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

export const PERMISSION_VALUES = Object.values(PERMISSIONS) as [Permission, ...Permission[]]

const P = PERMISSIONS

/** Everything within one competition, but never the creation of another. */
const COMPETITION_ADMIN_PERMISSIONS: readonly Permission[] = [
  P.COMPETITION_READ,
  P.COMPETITION_MANAGE,
  P.FORM_MANAGE,
  P.TEAM_READ,
  P.TEAM_READ_ALL,
  P.TEAM_MANAGE,
  P.SUBMISSION_READ,
  P.SUBMISSION_READ_ALL,
  P.SUBMISSION_MANAGE,
  P.FILE_READ,
  P.FILE_READ_ALL,
  P.FILE_MANAGE,
  P.JUDGE_MANAGE,
  P.ASSIGNMENT_MANAGE,
  P.SCORE_READ,
  P.SCORE_MANAGE,
  P.MANUAL_SCORE_ENTER,
  P.RESULT_READ,
  P.RESULT_MANAGE,
  P.ROLE_READ,
  P.ROLE_MANAGE,
  P.ANNOUNCEMENT_MANAGE,
  P.AUDIT_READ,
  P.ELIGIBILITY_REVIEW,
  P.ELIGIBILITY_MANAGE_POLICY,
  P.COMPETITION_CONFIG_EXPORT,
  P.COMPETITION_CLONE,
  P.TEMPLATE_MANAGE,
]

/**
 * Day-to-day operations: teams, submissions and reports. Staff deliberately
 * cannot change who holds power, alter the rubric, reopen a locked score, or
 * publish results.
 */
const STAFF_PERMISSIONS: readonly Permission[] = [
  P.COMPETITION_READ,
  P.TEAM_READ,
  P.TEAM_READ_ALL,
  P.TEAM_MANAGE,
  P.SUBMISSION_READ,
  P.SUBMISSION_READ_ALL,
  P.FILE_READ,
  P.FILE_READ_ALL,
  P.ROLE_READ,
  P.SCORE_READ,
  P.MANUAL_SCORE_ENTER,
  P.RESULT_READ,
  P.ANNOUNCEMENT_MANAGE,
]

/** Own team, own submission. Nothing about anybody else. */
const STUDENT_PERMISSIONS: readonly Permission[] = [
  P.COMPETITION_READ,
  P.TEAM_READ,
  P.SUBMISSION_READ,
  P.FILE_READ,
  P.RESULT_READ,
  P.ELIGIBILITY_READ_OWN,
  P.ELIGIBILITY_SUBMIT_OWN,
]

/** Accompanies a team: sees it, never submits on the students' behalf. */
const TEACHER_PERMISSIONS: readonly Permission[] = [
  P.COMPETITION_READ,
  P.TEAM_READ,
  P.SUBMISSION_READ,
  P.FILE_READ,
  P.RESULT_READ,
  P.ELIGIBILITY_READ_OWN,
  P.ELIGIBILITY_SUBMIT_OWN,
]

/**
 * Assigned submissions and their own scores, and nothing else. There is
 * deliberately no `team.read.all`, `submission.read.all` or `score.manage`
 * here: any of the three would defeat anonymous judging (invariant #4) or let
 * a judge see other judges' scores before their own work is done.
 */
const JUDGE_PERMISSIONS: readonly Permission[] = [
  P.COMPETITION_READ,
  P.SUBMISSION_REVIEW,
  P.FILE_READ,
  P.SCORE_SUBMIT,
]

const OBSERVER_PERMISSIONS: readonly Permission[] = [P.COMPETITION_READ, P.RESULT_READ]

export const ROLE_PERMISSIONS: Record<CompetitionRole, readonly Permission[]> = {
  [COMPETITION_ROLES.COMPETITION_ADMIN]: COMPETITION_ADMIN_PERMISSIONS,
  [COMPETITION_ROLES.STAFF]: STAFF_PERMISSIONS,
  [COMPETITION_ROLES.STUDENT]: STUDENT_PERMISSIONS,
  [COMPETITION_ROLES.TEACHER]: TEACHER_PERMISSIONS,
  [COMPETITION_ROLES.JUDGE]: JUDGE_PERMISSIONS,
  [COMPETITION_ROLES.OBSERVER]: OBSERVER_PERMISSIONS,
}

/**
 * Permissions no competition role can ever grant, because they are decisions
 * about the platform rather than about one competition.
 */
const PLATFORM_ONLY_PERMISSIONS: readonly Permission[] = [P.COMPETITION_CREATE]

export function permissionsForCompetitionRoles(
  roles: readonly CompetitionRole[],
): ReadonlySet<Permission> {
  const granted = new Set<Permission>()
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role]) {
      granted.add(permission)
    }
  }
  return granted
}

export interface PermissionSubject {
  readonly globalRole: GlobalRole
  /** Roles the user holds *in the competition currently being accessed*. */
  readonly competitionRoles: readonly CompetitionRole[]
}

/**
 * The single authorization predicate. A super admin passes everything; any
 * other account must hold a competition role that grants the permission.
 *
 * Holding a permission is only half of an authorization decision. The query
 * behind it must still be scoped: a grant in competition A never authorises a
 * read in competition B.
 */
export function hasPermission(subject: PermissionSubject, permission: Permission): boolean {
  if (subject.globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    return true
  }
  if (PLATFORM_ONLY_PERMISSIONS.includes(permission)) {
    return false
  }
  return permissionsForCompetitionRoles(subject.competitionRoles).has(permission)
}

export function hasAnyRole(subject: PermissionSubject, roles: readonly CompetitionRole[]): boolean {
  if (subject.globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    return true
  }
  return subject.competitionRoles.some((role) => roles.includes(role))
}

/**
 * Platform Administration capability vocabulary (SEC-006, API-contract §8,
 * security-privacy-ops §4). A `/api/platform/*` route must never test
 * `globalRole === 'super_admin'` directly — GOV-013 requires Platform
 * Administration authorization to go through a capability mapping exactly
 * like competition authorization does, so that a future lower-privilege
 * Platform Operator role can be introduced by editing
 * `PLATFORM_ROLE_PERMISSIONS` alone, without touching any route.
 *
 * The three read/manage pairs (users, memberships, judges, admins) exist
 * because "may see" and "may change" are different questions per the same
 * split already drawn for `rubric.read` vs `rubric.manage` at the competition
 * level. `*.manage` permissions are defined now so the constant vocabulary
 * is complete per the API contract baseline, even though no Wave K route
 * enforces them yet — mutation endpoints (Wave L) will.
 */
export const PLATFORM_PERMISSIONS = {
  DASHBOARD_READ: 'platform.dashboard.read',
  USERS_READ: 'platform.users.read',
  USERS_MANAGE: 'platform.users.manage',
  MEMBERSHIPS_READ: 'platform.memberships.read',
  MEMBERSHIPS_MANAGE: 'platform.memberships.manage',
  JUDGES_READ: 'platform.judges.read',
  JUDGES_MANAGE: 'platform.judges.manage',
  ADMINS_READ: 'platform.admins.read',
  ADMINS_MANAGE: 'platform.admins.manage',
  AUDIT_READ: 'platform.audit.read',
  OPERATIONS_READ: 'platform.operations.read',
  /** Wave O, GOV-019/020: review/approve/reject a Competition Creation Request. */
  COMPETITIONS_APPROVE: 'platform.competitions.approve',
  /** PR-130: create/edit/archive the platform-wide Discovery Category taxonomy. */
  DISCOVERY_MANAGE: 'platform.discovery.manage',
  /**
   * PRIV-011/SEC-041 (v3.86 Phase 4 retention). Manually trigger the
   * enrollment-proof retention sweep on demand — Platform Admin baseline
   * only, mirroring the `*.manage` (not `*.read`) pattern above: this is a
   * mutation (irreversible file deletion), not a read.
   */
  ELIGIBILITY_RETENTION_MANAGE: 'platform.eligibility_retention.manage',
} as const

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[keyof typeof PLATFORM_PERMISSIONS]

export const PLATFORM_PERMISSION_VALUES = Object.values(PLATFORM_PERMISSIONS) as [
  PlatformPermission,
  ...PlatformPermission[],
]

/**
 * Baseline mapping (API-contract §8, security-privacy-ops §4): `super_admin`
 * holds every platform permission. No other global role holds any of them
 * today — there is no "Platform Operator" role yet. Adding one later means
 * adding a branch here, not a new hard-coded check at each call site.
 */
const SUPER_ADMIN_PLATFORM_PERMISSIONS: readonly PlatformPermission[] = PLATFORM_PERMISSION_VALUES

export interface PlatformPermissionSubject {
  readonly globalRole: GlobalRole
}

/**
 * The Platform Administration authorization predicate, mirroring
 * `hasPermission` for competition scope. Deliberately a separate function
 * (not reusing `hasPermission`) because Platform Administration and
 * Competition Administration are different authorization domains per
 * GOV-013 — a competition role must never imply a platform capability, and
 * collapsing the two predicates into one is exactly the kind of drift this
 * separation prevents.
 */
export function hasPlatformPermission(
  subject: PlatformPermissionSubject,
  permission: PlatformPermission,
): boolean {
  if (subject.globalRole === GLOBAL_ROLES.SUPER_ADMIN) {
    return SUPER_ADMIN_PLATFORM_PERMISSIONS.includes(permission)
  }
  return false
}
